/**
 * Customer Active List Hook
 * Business logic for active customers list with expandable contracts
 */
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getCustomerActive } from '@/modules/Customer/services'
import type { CustomerActive } from '@/modules/Customer/types'
import usePermissionsStore from '@/stores/usePermissionsStore'
import {
    getCustomerActiveColumns,
    getServiceColumns,
    getCustomerActiveFilterItems,
    CUSTOMER_ACTIVE_FILTER_DEFAULTS,
    CUSTOMER_ACTIVE_SORT_CONFIG,
} from '../utils/customer-active-list-config'

/**
 * Filter interface for Customer Active list
 */
interface CustomerActiveFilters {
    customer_sale_followers: number[]
    customer_cs_followers: number[]
    customer_industry: string[]
    product_group_names: string[]
    customer_source: string[]
    customer_source_type: string[]
    has_sla: string | undefined
}

// Cột tiền ẩn với Marketing (chỉ xem KH convert từ lead)
const LEAD_ORIGIN_HIDDEN_COLUMN_KEYS = ['total_payment']
const LEAD_ORIGIN_HIDDEN_SERVICE_COLUMN_KEYS = ['service_price_register']

/**
 * Hook for managing Customer Active list
 * Handles data fetching, pagination, sorting, filtering, and expandable rows
 */
export const useCustomerActiveList = () => {
    const { t } = useTranslation('customer')
    const { canViewLeadOriginCustomers } = usePermissionsStore()

    // URL params for pagination, sorting and filters
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<CustomerActiveFilters>({
        defaults: {
            page: 1,
            page_size: 20,
            ...CUSTOMER_ACTIVE_SORT_CONFIG.defaultSort,
        },
        filterDefaults: CUSTOMER_ACTIVE_FILTER_DEFAULTS,
    })

    // Form for filter controls
    const { control } = useForm<CustomerActiveFilters>({
        defaultValues: filters,
        values: filters,
    })

    // Fetch customer active data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, 'active', queryParams],
        queryFn: () =>
            getCustomerActive({
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                sort_by: params.sort_by,
                sort_order: params.sort_order,
                filters: queryParams.filters,
            }),
        placeholderData: true,
    })

    const customers = data?.results || []
    const pagination = data?.pagination

    // Memoized columns
    const columns = useMemo(() => {
        const cols = getCustomerActiveColumns(t)
        if (!canViewLeadOriginCustomers) return cols
        return cols.filter(
            (col) => !LEAD_ORIGIN_HIDDEN_COLUMN_KEYS.includes(String(col.key))
        )
    }, [t, canViewLeadOriginCustomers])
    const serviceColumns = useMemo(() => {
        const cols = getServiceColumns(t)
        if (!canViewLeadOriginCustomers) return cols
        return cols.filter(
            (col) =>
                !LEAD_ORIGIN_HIDDEN_SERVICE_COLUMN_KEYS.includes(
                    String(col.key)
                )
        )
    }, [t, canViewLeadOriginCustomers])
    const filterItems = useMemo(() => getCustomerActiveFilterItems(t), [t])

    // Pagination handlers
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )

    // Sort handler
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    return {
        // Data
        customers,
        pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & filters
        params,
        filters,
        control,
        setFilter,
        searchInputProps,

        // Columns
        columns,
        serviceColumns,
        filterItems,

        // Handlers
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,

        // Translation
        t,
    }
}

/**
 * Get services from customer row for expandable
 */
export const getCustomerServices = (row: CustomerActive) => row.services || []

/**
 * Check if customer has services (for rowExpandable)
 */
export const hasServices = (row: CustomerActive) =>
    row.services && row.services.length > 0
